export class FormPluginList extends HTMLElement {
    static get observedAttributes() {
        return ['label', 'values', 'all-plugins'];
    }

    private _values: any[] = [];
    private _allPlugins: any[] = [];
    private _dragSrcIndex: number | null = null;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            if (name === 'values') {
                try {
                    this._values = JSON.parse(newValue);
                    if (!Array.isArray(this._values)) this._values = [];
                } catch (e) {
                    this._values = [];
                }
                this.render();
            } else if (name === 'all-plugins') {
                try {
                    this._allPlugins = JSON.parse(newValue);
                    if (!Array.isArray(this._allPlugins)) this._allPlugins = [];
                } catch (e) {
                    this._allPlugins = [];
                }
                this.render();
            } else {
                this.render();
            }
        }
    }

    get values() {
        return this._values;
    }

    set values(val: any[]) {
        this._values = val;
        this.render();
    }

    private handlePluginChange(index: number, pluginId: string) {
        if (this._values[index]) {
            const plugin = this._allPlugins.find(p => p.id === pluginId);
            this._values[index] = {
                plugin_id: pluginId,
                config: plugin?.config || {}
            };
            this.dispatchChange();
            this.render();
        }
    }

    private handleAdd() {
        this._values.push({ plugin_id: '', config: {} });
        this.render();
        this.dispatchChange();
    }

    private handleRemove(index: number) {
        this._values.splice(index, 1);
        this.render();
        this.dispatchChange();
    }

    private handleDragStart(e: DragEvent, index: number) {
        this._dragSrcIndex = index;
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', index.toString());
        }
        const item = (e.target as HTMLElement).closest('.list-item');
        if (item) {
            item.classList.add('dragging');
        }
    }

    private handleDragOver(e: DragEvent) {
        if (e.preventDefault) {
            e.preventDefault();
        }
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'move';
        }

        const item = (e.target as HTMLElement).closest('.list-item');
        if (item) {
            item.classList.add('over');
        }
        return false;
    }

    private handleDragLeave(e: DragEvent) {
        const item = (e.target as HTMLElement).closest('.list-item');
        if (item) {
            item.classList.remove('over');
        }
    }

    private handleDrop(e: DragEvent, targetIndex: number) {
        e.stopPropagation();
        e.preventDefault();

        this.shadowRoot?.querySelectorAll('.list-item').forEach(el => {
            el.classList.remove('over', 'dragging');
        });

        if (this._dragSrcIndex !== null && this._dragSrcIndex !== targetIndex) {
            const toMove = this._values[this._dragSrcIndex];
            this._values.splice(this._dragSrcIndex, 1);
            this._values.splice(targetIndex, 0, toMove);

            this.render();
            this.dispatchChange();
        }
        this._dragSrcIndex = null;
        return false;
    }

    private dispatchChange() {
        this.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            composed: true,
            detail: { values: this._values }
        }));
    }

    render() {
        const label = this.getAttribute('label') || '';
        const itemsToRender = this._values;

        if (this.shadowRoot) {
            this.shadowRoot.innerHTML = `
                <style>
                    :host {
                        display: block;
                        margin-bottom: 1.5rem;
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .form-group {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }
                    label {
                        color: #cbd5e1;
                        font-size: 0.9rem;
                        font-weight: 600;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .list-container {
                        display: flex;
                        flex-direction: column;
                        gap: 0.75rem;
                    }
                    .list-item {
                        display: flex;
                        gap: 0.75rem; 
                        align-items: center;
                        background: rgba(255, 255, 255, 0.03);
                        border: 1px solid rgba(255, 255, 255, 0.05);
                        border-radius: 8px;
                        padding: 0.5rem;
                        transition: all 0.2s;
                    }
                    .list-item.dragging {
                        opacity: 0.4;
                    }
                    .list-item.over {
                        border-color: var(--accent, #f59e0b);
                        background: rgba(245, 158, 11, 0.05);
                    }
                    .drag-handle {
                        cursor: grab;
                        color: #64748b;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 0.25rem;
                        opacity: 0.5;
                        transition: all 0.2s;
                    }
                    .drag-handle:hover {
                        opacity: 1;
                        color: white;
                    }
                    select {
                        flex: 1;
                        padding: 0.6rem;
                        background: rgba(255, 255, 255, 0.05);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 6px;
                        color: white;
                        font-size: 0.9rem;
                        font-family: inherit;
                        box-sizing: border-box;
                        cursor: pointer;
                    }
                    select:focus {
                        outline: none;
                        border-color: var(--accent, #f59e0b);
                    }
                    select option {
                        background: #1e293b;
                        color: white;
                    }
                    select option:disabled {
                        color: #475569;
                    }
                    .btn-remove {
                        background: transparent;
                        border: 1px solid transparent;
                        color: #94a3b8;
                        width: 32px;
                        height: 32px;
                        border-radius: 6px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.2s;
                        padding: 0;
                    }
                    .btn-remove:hover {
                        background: rgba(239, 68, 68, 0.1);
                        color: #ef4444;
                        border-color: rgba(239, 68, 68, 0.2);
                    }
                    .btn-add {
                        background: transparent;
                        border: 1px dashed rgba(255, 255, 255, 0.2);
                        color: #94a3b8;
                        padding: 0.75rem;
                        border-radius: 8px;
                        cursor: pointer;
                        width: 100%;
                        margin-top: 0.5rem;
                        font-size: 0.85rem;
                        font-weight: 600;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                        transition: all 0.2s;
                    }
                    .btn-add:hover {
                        border-color: var(--accent, #f59e0b);
                        color: white;
                        background: rgba(255, 255, 255, 0.02);
                    }
                    .empty-state {
                        padding: 1.5rem;
                        text-align: center;
                        background: rgba(255,255,255,0.02);
                        border: 1px dashed rgba(255,255,255,0.1);
                        border-radius: 8px;
                        color: #64748b;
                        font-size: 0.85rem;
                    }
                </style>
                <div class="form-group">
                    <label>${label}</label>
                    <div class="list-container">
                        ${itemsToRender.length === 0 ? `
                            <div class="empty-state">No plugins attached.</div>
                        ` : itemsToRender.map((item: any, i: number) => {
                const selectedIds = itemsToRender.map(it => it.plugin_id).filter(id => id !== "" && id !== item.plugin_id);

                return `
                            <div class="list-item" data-index="${i}">
                                <div class="drag-handle" draggable="true" data-index="${i}">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <circle cx="9" cy="5" r="1" />
                                        <circle cx="9" cy="12" r="1" />
                                        <circle cx="9" cy="19" r="1" />
                                        <circle cx="15" cy="5" r="1" />
                                        <circle cx="15" cy="12" r="1" />
                                        <circle cx="15" cy="19" r="1" />
                                    </svg>
                                </div>
                                <select data-index="${i}">
                                    <option value="">-- Select a plugin --</option>
                                    ${this._allPlugins.map(p => {
                    const isSelectedElsewhere = selectedIds.includes(p.id);
                    return `
                                            <option value="${p.id}" ${p.id === item.plugin_id ? 'selected' : ''} ${isSelectedElsewhere ? 'disabled' : ''}>
                                                ${p.name} (${p.type})${isSelectedElsewhere ? ' (Already added)' : ''}
                                            </option>
                                        `;
                }).join('')}
                                </select>
                                <button type="button" class="btn-remove" data-index="${i}">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                </button>
                            </div>
                        `;
            }).join('')}
                    </div>
                    <button type="button" class="btn-add" id="btnAdd">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        Add Plugin
                    </button>
                </div>
            `;

            this.shadowRoot.querySelectorAll('select').forEach(select => {
                select.addEventListener('change', (e) => {
                    const target = e.target as HTMLSelectElement;
                    const idx = parseInt(target.dataset.index || '0');
                    this.handlePluginChange(idx, target.value);
                });
            });

            this.shadowRoot.querySelectorAll('.drag-handle').forEach(handle => {
                const idx = parseInt((handle as HTMLElement).dataset.index || '0');
                handle.addEventListener('dragstart', (e) => this.handleDragStart(e as DragEvent, idx));
            });

            this.shadowRoot.querySelectorAll('.list-item').forEach(item => {
                const idx = parseInt((item as HTMLElement).dataset.index || '0');
                item.addEventListener('dragover', (e) => this.handleDragOver(e as DragEvent));
                item.addEventListener('dragleave', (e) => this.handleDragLeave(e as DragEvent));
                item.addEventListener('drop', (e) => this.handleDrop(e as DragEvent, idx));
                item.addEventListener('dragend', (e) => {
                    const listItem = (e.target as HTMLElement).closest('.list-item');
                    if (listItem) listItem.classList.remove('dragging');
                    this.shadowRoot?.querySelectorAll('.over').forEach(el => el.classList.remove('over'));
                });
            });

            this.shadowRoot.querySelectorAll('.btn-remove').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const target = (e.target as HTMLElement).closest('.btn-remove') as HTMLButtonElement;
                    if (target) {
                        const idx = parseInt(target.dataset.index || '0');
                        this.handleRemove(idx);
                    }
                });
            });

            this.shadowRoot.getElementById('btnAdd')?.addEventListener('click', () => {
                this.handleAdd();
            });
        }
    }
}

customElements.define('form-plugin-list', FormPluginList);
